import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { setNotification } from '../../../redux/notificationSlice'
import { useRequest } from '../../../hooks/UseRequest'
import { buildQueryString } from '../../../helpers/formatting'
import { statusMeta } from './blockChangeHelpers'
import { useAuthReady } from './useAuthReady'

const BASE = () => `${process.env.REACT_APP_API_DOMAIN}/lead_funnel/blocks`
const CHANGE_REQUESTS = () =>
  `${process.env.REACT_APP_API_DOMAIN}/lead_funnel/block_change_requests`

const explorerViewKeyFor = ({ treatmentId, customerType, changeRequestId }) =>
  `${treatmentId}:${customerType}:${changeRequestId ?? ''}`

// One useRequest per concern: its endpoint lives in a per-render closure, so parallel calls must not share one.
// Without a treatment in the URL, the tree's root is the default view; nothing is fetched until the tree arrives.
// With a change request, the explorer and detail show that draft over the live tables; parity stays live-only.
export function useBlocksExplorerData({
  requestedTreatmentId,
  customerType,
  block,
  changeRequestId,
  draftAuthorId,
}) {
  const dispatch = useDispatch()
  const navi = useNavigate()
  const treeRequest = useRequest()
  const vocabularyRequest = useRequest()
  const explorerRequest = useRequest()
  const parityRequest = useRequest()
  const detailRequest = useRequest()
  const checksumRequest = useRequest()
  const draftRequest = useRequest()
  const myDraftsRequest = useRequest()

  const [tree, setTree] = useState(null)
  const [treeFailed, setTreeFailed] = useState(false)
  const [treeAttempt, setTreeAttempt] = useState(0)
  const [vocabulary, setVocabulary] = useState(null)
  const [vocabularyFailed, setVocabularyFailed] = useState(false)
  const [vocabularyAttempt, setVocabularyAttempt] = useState(0)
  const [explorer, setExplorer] = useState(null)
  const [explorerViewKey, setExplorerViewKey] = useState(null)
  const [isExplorerLoading, setIsExplorerLoading] = useState(false)
  const [explorerErrorKey, setExplorerErrorKey] = useState(null)
  const [explorerConflictKey, setExplorerConflictKey] = useState(null)
  const [explorerAttempt, setExplorerAttempt] = useState(0)
  const [parity, setParity] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailKey, setDetailKey] = useState(null)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [detailErrorKey, setDetailErrorKey] = useState(null)
  const [detailAttempt, setDetailAttempt] = useState(0)
  const [checksum, setChecksum] = useState(null)
  const [draft, setDraft] = useState(null)
  const [myDrafts, setMyDrafts] = useState([])
  const treatmentId = requestedTreatmentId ?? tree?.nodes[0]?.id ?? null
  // Without a treatment nothing below ever loads, so a failed or empty tree is the view's load failure.
  const hasNoTreatment =
    treatmentId === null && (treeFailed || tree?.nodes?.length === 0)
  const viewKey = explorerViewKeyFor({
    treatmentId,
    customerType,
    changeRequestId,
  })
  const blockKey = block ? `${block.table}:${block.id}` : null
  const currentDetailKey = `${viewKey}|${blockKey}`
  const current = useRef({})
  current.current = { viewKey, blockKey, changeRequestId }
  // Latest request wins per resource: an effect load and refresh() calls can overlap for the same view.
  const sequence = useRef({ explorer: 0, draft: 0, detail: 0 })
  const begin = (resource) => {
    sequence.current[resource] += 1
    const sent = sequence.current[resource]
    return () => sequence.current[resource] === sent
  }

  // A closed request answers the draft view with 409; the page says so itself, so no toast is raised.
  const isClosedDraftError = (error) =>
    Boolean(changeRequestId) && error?.response?.status === 409

  const handleError = (error, fallback) => {
    const status = error?.response?.status
    if (status === 401 || status === 403) {
      navi('/403')
      return
    }
    if (isClosedDraftError(error)) return
    dispatch(
      setNotification({
        type: 'error',
        body: error?.response?.data?.message ?? fallback,
      })
    )
  }

  const fetchData = (request, url) => {
    request.setEndpoint(url)
    return request.get().then((res) => res?.data?.data ?? null)
  }

  const explorerUrl = () =>
    `${BASE()}/explorer${buildQueryString({
      treatment_id: treatmentId,
      customer_type: customerType,
      change_request_id: changeRequestId,
    })}`
  const detailUrl = () =>
    `${BASE()}/${block.table}/${block.id}${buildQueryString({
      treatment_id: treatmentId,
      customer_type: customerType,
      change_request_id: changeRequestId,
    })}`
  const draftUrl = () => `${CHANGE_REQUESTS()}/${changeRequestId}`

  const ready = useAuthReady()

  useEffect(() => {
    if (!ready) return
    let isStale = false
    setTreeFailed(false)
    fetchData(treeRequest, `${BASE()}/treatments`)
      .then((data) => !isStale && setTree(data))
      .catch((error) => {
        if (isStale) return
        setTreeFailed(true)
        handleError(error, 'Failed to load treatments')
      })
    return () => {
      isStale = true
    }
    // useRequest() recreates its methods every render; they do not belong here.
  }, [ready, treeAttempt])

  useEffect(() => {
    if (!ready) return
    let isStale = false
    setVocabularyFailed(false)
    fetchData(vocabularyRequest, `${BASE()}/vocabulary`)
      .then((data) => !isStale && setVocabulary(data))
      .catch((error) => {
        if (isStale) return
        setVocabularyFailed(true)
        handleError(error, 'Failed to load block vocabulary')
      })
    return () => {
      isStale = true
    }
  }, [ready, vocabularyAttempt])

  useEffect(() => {
    if (!ready) return
    let isStale = false
    // The drift banner is advisory; any failure (including the tables not existing yet) just hides it.
    fetchData(checksumRequest, `${BASE()}/checksum`)
      .then((data) => !isStale && setChecksum(data))
      .catch(() => !isStale && setChecksum(null))
    return () => {
      isStale = true
    }
    // useRequest() recreates its methods every render; they do not belong here.
  }, [ready])

  // Each load below drops its response, and leaves the loading flag alone, once its view or block has moved on.
  useEffect(() => {
    if (!ready || treatmentId === null) return
    let isStale = false
    const query = buildQueryString({
      treatment_id: treatmentId,
      customer_type: customerType,
    })
    const isLatest = begin('explorer')
    setIsExplorerLoading(true)
    setExplorerErrorKey(null)
    setExplorerConflictKey(null)
    fetchData(explorerRequest, explorerUrl())
      .then((data) => {
        if (isStale || !isLatest()) return
        setExplorer(data)
        setExplorerViewKey(viewKey)
      })
      .catch((error) => {
        if (isStale || !isLatest()) return
        setExplorerErrorKey(viewKey)
        if (isClosedDraftError(error)) setExplorerConflictKey(viewKey)
        handleError(error, 'Failed to load blocks')
      })
      .finally(() => {
        if (!isStale) setIsExplorerLoading(false)
      })
    setParity(null)
    if (!changeRequestId) {
      // Parity is advisory: a DS failure arrives as status "unavailable", and any other error just hides the badge.
      fetchData(parityRequest, `${BASE()}/parity${query}`)
        .then((data) => !isStale && setParity(data))
        .catch(() => !isStale && setParity({ status: 'unavailable' }))
    }
    return () => {
      isStale = true
    }
  }, [ready, treatmentId, customerType, changeRequestId, explorerAttempt])

  useEffect(() => {
    setDraft(null)
    if (!ready || !changeRequestId) return
    let isStale = false
    const isLatest = begin('draft')
    fetchData(draftRequest, draftUrl())
      .then((data) => !isStale && isLatest() && setDraft(data))
      .catch((error) => {
        if (!isStale && isLatest()) {
          handleError(error, 'Failed to load the draft')
        }
      })
    return () => {
      isStale = true
    }
  }, [ready, changeRequestId])

  useEffect(() => {
    if (!ready || !draftAuthorId) return
    myDraftsRequest.setEndpoint(
      `${CHANGE_REQUESTS()}${buildQueryString({
        author_id: draftAuthorId,
        per_page: 100,
      })}`
    )
    const keepOpen = (changeRequests) =>
      changeRequests.filter(
        (changeRequest) => statusMeta(changeRequest.status).isOpen
      )
    myDraftsRequest
      .get()
      .then((res) => setMyDrafts(keepOpen(res?.data?.data ?? [])))
      .catch(() => setMyDrafts([]))
  }, [ready, draftAuthorId])

  useEffect(() => {
    setDetail(null)
    if (!ready || treatmentId === null || !block) {
      setIsDetailLoading(false)
      return
    }
    let isStale = false
    const key = currentDetailKey
    const isLatest = begin('detail')
    setIsDetailLoading(true)
    setDetailErrorKey(null)
    fetchData(detailRequest, detailUrl())
      .then((data) => {
        if (isStale || !isLatest()) return
        setDetail(data)
        setDetailKey(key)
      })
      .catch((error) => {
        if (isStale || !isLatest()) return
        setDetailErrorKey(key)
        handleError(error, 'Failed to load block detail')
      })
      .finally(() => {
        if (!isStale) setIsDetailLoading(false)
      })
    return () => {
      isStale = true
    }
  }, [
    ready,
    treatmentId,
    customerType,
    block?.table,
    block?.id,
    changeRequestId,
    detailAttempt,
  ])

  // Re-reads what a draft change affects without the loading states, so the list and drawer stay on screen.
  // It may run from an older render, so a resource whose view or block has moved on is left alone: bumping its
  // counter would discard the current view's own in-flight load along with this stale one.
  const refresh = () => {
    // The keys of the render the URLs below come from, not the latest one: that is what makes a late response stale.
    const sent = { viewKey, blockKey, changeRequestId }
    const isViewCurrent = () => current.current.viewKey === sent.viewKey
    const isDraftCurrent = () =>
      current.current.changeRequestId === sent.changeRequestId
    const isBlockCurrent = () =>
      isViewCurrent() && current.current.blockKey === sent.blockKey
    const sentDetailKey = currentDetailKey
    if (vocabularyFailed) setVocabularyAttempt((attempt) => attempt + 1)
    if (treatmentId !== null && isViewCurrent()) {
      const isExplorerLatest = begin('explorer')
      fetchData(explorerRequest, explorerUrl())
        .then((data) => {
          if (!isViewCurrent() || !isExplorerLatest()) return
          setExplorer(data)
          setExplorerViewKey(sent.viewKey)
          setExplorerErrorKey(null)
          setExplorerConflictKey(null)
        })
        .catch((error) => {
          if (!isViewCurrent() || !isExplorerLatest()) return
          setExplorerErrorKey(sent.viewKey)
          if (isClosedDraftError(error)) setExplorerConflictKey(sent.viewKey)
          handleError(error, 'Failed to load blocks')
        })
    }
    if (changeRequestId && isDraftCurrent()) {
      const isDraftLatest = begin('draft')
      fetchData(draftRequest, draftUrl())
        .then((data) => {
          if (isDraftCurrent() && isDraftLatest()) setDraft(data)
        })
        .catch((error) => {
          if (isDraftCurrent() && isDraftLatest()) {
            handleError(error, 'Failed to load the draft')
          }
        })
    }
    if (block && treatmentId !== null && isBlockCurrent()) {
      const isDetailLatest = begin('detail')
      fetchData(detailRequest, detailUrl())
        .then((data) => {
          if (!isBlockCurrent() || !isDetailLatest()) return
          setDetail(data)
          setDetailKey(sentDetailKey)
          setDetailErrorKey(null)
        })
        .catch((error) => {
          if (!isBlockCurrent() || !isDetailLatest()) return
          setDetailErrorKey(sentDetailKey)
          handleError(error, 'Failed to load block detail')
        })
    }
  }

  const loadedDraft = draft?.change_request?.id === changeRequestId ? draft : null
  const isDraftClosed =
    Boolean(changeRequestId) &&
    ((loadedDraft !== null &&
      !statusMeta(loadedDraft.change_request.status).isOpen) ||
      explorerConflictKey === viewKey)

  return {
    treatmentId,
    tree,
    vocabulary,
    explorer,
    isExplorerLoading,
    // True once the loaded list belongs to the treatment, customer type and draft now in view.
    isExplorerCurrent: explorer !== null && explorerViewKey === viewKey,
    explorerLoadFailed:
      hasNoTreatment || (explorerErrorKey === viewKey && !isExplorerLoading),
    retryExplorer: () => {
      if (hasNoTreatment) setTreeAttempt((attempt) => attempt + 1)
      else setExplorerAttempt((attempt) => attempt + 1)
    },
    treeLoadFailed: treeFailed,
    retryTree: () => setTreeAttempt((attempt) => attempt + 1),
    viewKey,
    parity,
    // Effects clear these a commit late, so a previous block's or draft's data is never handed out meanwhile.
    detail: detailKey === currentDetailKey ? detail : null,
    isDetailLoading,
    detailLoadFailed: detailErrorKey === currentDetailKey && !isDetailLoading,
    retryDetail: () => setDetailAttempt((attempt) => attempt + 1),
    checksum,
    draft: loadedDraft,
    // The request in the URL is applied, rejected or discarded, so it has no draft view to show.
    isDraftClosed,
    vocabularyLoadFailed: vocabularyFailed,
    retryVocabulary: () => setVocabularyAttempt((attempt) => attempt + 1),
    myDrafts,
    refresh,
  }
}
